// src/pages/CreateStudentFee.jsx
import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import SummaryApi from '../common/index';
import paymentscan from "../assets/paymentscan.jpg";

const CreateStudentFee = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const encodedId = queryParams.get('id');
  const encodedName = queryParams.get('name');
  let student_id = '';
  let student_name = 'Unknown Student';
  try {
    if (encodedId) student_id = atob(encodedId);
  } catch (e) {
    console.error("Invalid student ID", e);
    toast.error("Invalid student link.");
  }
  try {
    if (encodedName) student_name = atob(encodedName);
  } catch (e) {
    console.error("Invalid student name", e);
  }
  const APPLICATION_FEE = 1000;
  const [formData, setFormData] = useState({
    utr_number: '',
    transaction_id: '',
    payment_receipt: null,
  });
  const [studentDetails, setStudentDetails] = useState({
    student_id: '',
    student_name: '',
    father_name: '',
    mother_name: '',
    date_of_birth: '',
    caste: '',
    religion: '',
    email: '',
    degree: '',
    stream: '',
    academic_year: '',
    mobile_number: '',
    alternate_mobile_number: '',
    state: '',
    district: '',
    city: '',
    village: '',
    pincode: '',
    aadhar: '',
  });
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const receiptRef = useRef();
  
  // Fetch student details on component mount
  useEffect(() => {
    if (student_id) {
      fetchStudentDetails();
    }
  }, [student_id]);

  const fetchStudentDetails = async () => {
    try {
      const res = await fetch(SummaryApi.getstudentByid.url, {
        method: SummaryApi.getstudentByid.method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ student_id: student_id }),
      });
     
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
     
      const result = await res.json();
      if (result.status === true && result.data) {
        const student = result.data;
        setStudentDetails({
          student_id: student.student_id || '',
          student_name: student.student_name || '',
          father_name: student.father_name || '',
          mother_name: student.mother_name || '',
          date_of_birth: student.date_of_birth || '',
          caste: student.caste || '',
          religion: student.religion || '',
          email: student.email || '',
          degree: student.degree || '',
          stream: student.stream || '',
          academic_year: student.academic_year || '',
          mobile_number: student.mobile_number || '',
          alternate_mobile_number: student.alternate_mobile_number || '',
          state: student.state || '',
          district: student.district || '',
          city: student.city || '',
          village: student.village || '',
          pincode: student.pincode || '',
          aadhar: student.aadhar || '',
        });
      }
    } catch (err) {
      console.error("Fetch student error:", err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData((prev) => ({ ...prev, payment_receipt: file }));
      const reader = new FileReader();
      reader.onloadend = () => setPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const validateForm = () => {
    if (!formData.utr_number.trim()) {
      toast.error("UTR Number is required");
      return false;
    }
    if (!formData.transaction_id.trim()) {
      toast.error("Transaction ID is required");
      return false;
    }
    if (!formData.payment_receipt) {
      toast.error("Payment receipt image is required");
      return false;
    }
    if (!student_id) {
      toast.error("Student ID is missing");
      return false;
    }
    return true;
  };

  // ==================== CALL EMAIL API AFTER PAYMENT (x-www-form-urlencoded format) ====================
  const sendPaymentConfirmationEmail = async () => {
    try {
      // Send required fields including amount
      const emailPayload = {
        student_name: studentDetails.student_name,
        email: studentDetails.email,
        mobile_number: studentDetails.mobile_number,
        transaction_id: formData.transaction_id,
        utr_number: formData.utr_number,
        amount: APPLICATION_FEE
      };

      // Convert to x-www-form-urlencoded
      const formBody = new URLSearchParams();
      formBody.append('student_name', emailPayload.student_name);
      formBody.append('email', emailPayload.email);
      formBody.append('mobile_number', emailPayload.mobile_number);
      formBody.append('transaction_id', emailPayload.transaction_id);
      formBody.append('utr_number', emailPayload.utr_number);
      formBody.append('amount', emailPayload.amount.toString());

      const res = await fetch(SummaryApi.paymentdonemailsend.url, {
        method: SummaryApi.paymentdonemailsend.method,
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });
      
      const result = await res.json();
      if (result.success || result.status) {
        console.log("✅ Confirmation email sent successfully");
      } else {
        console.warn("⚠️ Email API responded but may have failed:", result);
      }
    } catch (err) {
      console.error("Failed to send confirmation email:", err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    try {
      // Create FormData object like in StudentRegistration
      const submitData = new FormData();
     
      // Append all student details including caste and religion
      submitData.append('student_id', student_id);
      submitData.append('student_name', studentDetails.student_name);
      submitData.append('father_name', studentDetails.father_name);
      submitData.append('mother_name', studentDetails.mother_name);
      submitData.append('date_of_birth', studentDetails.date_of_birth);
      submitData.append('caste', studentDetails.caste || '');
      submitData.append('religion', studentDetails.religion || '');
      submitData.append('email', studentDetails.email);
      submitData.append('degree', studentDetails.degree);
      submitData.append('stream', studentDetails.stream);
      submitData.append('academic_year', studentDetails.academic_year);
      submitData.append('mobile_number', studentDetails.mobile_number);
      submitData.append('alternate_mobile_number', studentDetails.alternate_mobile_number);
      submitData.append('state', studentDetails.state);
      submitData.append('district', studentDetails.district);
      submitData.append('city', studentDetails.city);
      submitData.append('village', studentDetails.village);
      submitData.append('pincode', studentDetails.pincode);
      submitData.append('aadhar', studentDetails.aadhar);
     
      // Append payment details
      submitData.append('amount', APPLICATION_FEE);
      submitData.append('utr_number', formData.utr_number);
      submitData.append('transaction_id', formData.transaction_id);
      submitData.append('payment_receipt', formData.payment_receipt);
     
      // Add is_paid = 1 (payment_date will be handled by backend automatically)
      submitData.append('is_paid', '1');
      
      const res = await fetch(SummaryApi.createpaymentstudent.url, {
        method: SummaryApi.createpaymentstudent.method,
        body: submitData,
      });
      
      if (!res.ok) throw new Error(`Server error: ${res.status}`);
      
      const result = await res.json();
      const isSuccess = result.success === true || result.status === true;
      
      if (isSuccess) {
        toast.success(result.message || "Payment completed successfully!");
        // Call Email API with all student details after successful payment
        await sendPaymentConfirmationEmail();
        navigate('/student-report');
      } else {
        toast.error(result.message || "Payment creation failed");
      }
    } catch (err) {
      console.error("Submission error:", err);
      toast.error(err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (student_id && student_name) {
      const encodedId = btoa(student_id.toString());
      const encodedName = btoa(student_name);
      navigate(`/student-registration?id=${encodedId}&name=${encodedName}`);
    } else {
      navigate('/dashboard');
    }
  };

  const isSubmitDisabled = !formData.utr_number.trim() ||
                           !formData.transaction_id.trim() ||
                           !formData.payment_receipt ||
                           loading;

  const inputClass = "block w-full border border-gray-300 rounded px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none";
  const labelClass = "block text-sm font-medium text-gray-700 mb-1";
  const disabledInputClass = `${inputClass} bg-gray-100 cursor-not-allowed`;

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="bg-white shadow-lg rounded-xl p-6 md:p-8 border border-gray-200 relative">
        <button
          onClick={handleBack}
          className="absolute top-6 right-6 px-5 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition flex items-center gap-2 shadow-sm"
        >
          ← Back
        </button>
        <h2 className="text-3xl font-bold text-center text-blue-900 mb-2">
          Student Fee Payment {student_name !== 'Unknown Student' ? `- ${student_name}` : ''}
        </h2>
        <form onSubmit={handleSubmit} className="space-y-12">
          <section>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* QR Code Section */}
              <div className="space-y-6">
                <h3 className="text-xl font-semibold text-gray-800 mb-5 border-b pb-2">Scan QR Code</h3>
                <div className="border border-gray-300 rounded-lg p-6 text-center bg-gray-50">
                  <img
                    src={paymentscan}
                    alt="Scan QR Code"
                    className="mx-auto w-full max-w-sm h-auto object-contain"
                    style={{ maxHeight: '400px' }}
                  />
                  <p className="text-sm text-gray-500 mt-4">Scan this QR code to make payment</p>
                </div>
               
                {/* UTR and Transaction Details moved below QR code */}
                <div className="mt-6 space-y-4">
                  <div>
                    <label className={labelClass}>UTR Number <span style={{ color: 'red' }}>*</span></label>
                    <input
                      name="utr_number"
                      value={formData.utr_number}
                      onChange={handleChange}
                      className={inputClass}
                      placeholder="Enter UTR Number"
                      required
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Transaction ID <span style={{ color: 'red' }}>*</span></label>
                    <input
                      name="transaction_id"
                      value={formData.transaction_id}
                      onChange={handleChange}
                      className={inputClass}
                      placeholder="Enter Transaction ID"
                      required
                    />
                  </div>
                </div>
              </div>
              
              {/* Payment Form */}
              <div className="space-y-6">
                {/* Fee Details */}
                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-5 border-b pb-2">Fee Details</h3>
                  <div className="space-y-4">
                    <div className="flex items-center gap-4">
                      <label className={`${labelClass} w-40 flex-shrink-0`}>Application Fee</label>
                      <input
                        value={`₹ ${APPLICATION_FEE}`}
                        disabled
                        className={`${disabledInputClass} flex-1 font-semibold`}
                      />
                    </div>
                  </div>
                </div>
                
                {/* Upload Receipt */}
                <div className="space-y-3">
                  <h3 className="text-xl font-semibold text-gray-800 mb-5 border-b pb-2">Upload Receipt</h3>
                  <label className={labelClass}>
                    Attach Payment Receipt <span style={{ color: 'red' }}>*</span>
                  </label>
                  <div
                    className="border-2 border-dashed border-gray-300 rounded p-4 text-center cursor-pointer hover:border-blue-500"
                    onClick={() => receiptRef.current.click()}
                  >
                    {formData.payment_receipt ? (
                      <p className="text-sm text-green-600">Selected: {formData.payment_receipt.name}</p>
                    ) : (
                      <p className="text-sm text-gray-500">Click to upload payment receipt (jpg/png)</p>
                    )}
                    <input
                      ref={receiptRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleFileChange}
                    />
                  </div>
                  {preview && (
                    <div className="mt-2">
                      <p className="text-sm font-medium text-gray-700 mb-1">Preview:</p>
                      <div className="border border-gray-200 rounded overflow-hidden max-w-xs mx-auto md:mx-0">
                        <img src={preview} alt="Receipt preview" className="w-full h-auto max-h-48 object-contain" />
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Submit Button */}
                <div className="flex justify-center pt-6">
                  <button
                    type="submit"
                    disabled={isSubmitDisabled}
                    className={`px-12 py-3 rounded-lg text-white font-semibold text-lg w-full max-w-xs transition shadow-md ${
                      isSubmitDisabled ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-700 hover:bg-blue-800'
                    }`}
                  >
                    {loading ? 'Processing Payment...' : `SUBMIT PAYMENT`}
                  </button>
                </div>
                <strong className='text-red-600 text-xs block mt-4 text-center'>
                  Note: Please make sure to attach your payment receipt and enter correct UTR and Transaction ID.
                </strong>
              </div>
            </div>
          </section>
        </form>
      </div>
    </div>
  );
};

export default CreateStudentFee;